import { useState, useCallback } from 'react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useFinancialStatement } from '../hooks/useFinancialStatement';
import { useAuth } from '../contexts/AuthContext';
import { StatementFilters } from '../components/financial-statement/StatementFilters';
import { IncomeStatementTab } from '../components/financial-statement/IncomeStatementTab';
import { BalanceSheetTab } from '../components/financial-statement/BalanceSheetTab';
import { LineConfigTab } from '../components/financial-statement/LineConfigTab';
import { AccountMappingTab } from '../components/financial-statement/AccountMappingTab';
import { ViewBuilderTab } from '../components/financial-statement/ViewBuilderTab';
import { RepostingsTab } from '../components/financial-statement/RepostingsTab';
import { DrillThroughModal } from '../components/financial-statement/DrillThroughModal';
import { TransactionDrillModal } from '../components/financial-statement/TransactionDrillModal';
import { CommentPanel } from '../components/financial-statement/CommentPanel';
import { RefreshButton } from '../components/financial-statement/RefreshButton';
import type { FinancialStatementLine, LineAccount, FSTab } from '../types/financialStatement';

const TABS: { key: FSTab; label: string }[] = [
    { key: 'income-statement', label: 'Income Statement' },
    { key: 'balance-sheet', label: 'Balance Sheet' },
    { key: 'repostings', label: 'Repostings' },
    { key: 'statement-views', label: 'Statement Views' },
    { key: 'line-config', label: 'Line Configuration' },
    { key: 'account-mapping', label: 'Account Mapping' },
];

export function FinancialStatementPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/financial-statement');
    const { user } = useAuth();
    const {
        incomeStatementLines, assetLines, liabilityLines,
        filterOptions, filters, updateFilters,
        loading, error, reload,
        // View-related
        incomeViews, balanceViews,
        selectedIncomeViewId, selectedBalanceViewId,
        setSelectedIncomeViewId, setSelectedBalanceViewId,
        renderedIncomeLines, renderedBalanceLines,
        reloadViews,
    } = useFinancialStatement();

    const [activeTab, setActiveTab] = useState<FSTab>('income-statement');
    const [hideZeros, setHideZeros] = useState(true);

    // Drillthrough state - supports both legacy FinancialStatementLine and view-driven clicks
    const [drillLine, setDrillLine] = useState<FinancialStatementLine | null>(null);
    const [drillAccount, setDrillAccount] = useState<{ nr: string; name: string } | null>(null);

    // Comment panel state
    const [commentScope, setCommentScope] = useState<{
        lineNr: number;
        accountNr?: string;
        label: string;
    } | null>(null);

    // View-driven click handlers (receive lineNr + displayName)
    const handleViewLineClick = useCallback((lineNr: number, displayName: string) => {
        // Find the actual line from legacy data for full drillthrough support
        const legacyLine = [...(incomeStatementLines || []), ...(assetLines || []), ...(liabilityLines || [])]
            .find(l => l.LineNr === lineNr && !l.IsSubtotal);
        if (legacyLine) {
            setDrillLine(legacyLine);
        } else {
            // Build a minimal line for drillthrough
            setDrillLine({
                LineNr: lineNr,
                LineNameGerman: displayName,
                LineNameEnglish: displayName,
                LineType: 'ProfitLoss',
                SumLines: null,
                IsSubtotal: 0,
                CurrentYearAmount: 0,
                ComparisonYearAmount: 0,
                VarianceCHF: 0,
                VariancePct: null,
                CommentCount: 0,
            });
        }
    }, [incomeStatementLines, assetLines, liabilityLines]);

    const handleViewCommentClick = useCallback((lineNr: number, displayName: string) => {
        setCommentScope({
            lineNr,
            label: `Line ${lineNr} - ${displayName}`,
        });
    }, []);

    const handleDrillCommentClick = useCallback((lineNr: number, accountNr?: string) => {
        setCommentScope({
            lineNr,
            accountNr,
            label: accountNr
                ? `Account ${accountNr} (Line ${lineNr})`
                : `Line ${lineNr}`,
        });
    }, []);

    const handleAccountClick = useCallback((account: LineAccount) => {
        setDrillAccount({ nr: account.AccountNr, name: account.AccountName });
    }, []);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Financial Statement</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Income Statement and Balance Sheet with year-over-year comparison
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <RefreshButton company={filters.company} onComplete={reload} canEdit={canEdit} />
                    {canEdit && (
                        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            Edit Access
                        </span>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <StatementFilters
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={updateFilters}
                    onRefresh={reload}
                    loading={loading}
                    hideZeros={hideZeros}
                    onHideZerosChange={setHideZeros}
                    incomeViews={incomeViews}
                    balanceViews={balanceViews}
                    selectedIncomeViewId={selectedIncomeViewId}
                    selectedBalanceViewId={selectedBalanceViewId}
                    onIncomeViewChange={setSelectedIncomeViewId}
                    onBalanceViewChange={setSelectedBalanceViewId}
                />
            </div>

            {/* Error */}
            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b">
                    <nav className="flex -mb-px">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-4">
                    {activeTab === 'income-statement' && (
                        <IncomeStatementTab
                            lines={renderedIncomeLines}
                            year={filters.year}
                            comparisonYear={filters.comparisonYear}
                            onLineClick={handleViewLineClick}
                            onCommentClick={handleViewCommentClick}
                            loading={loading}
                            hideZeros={hideZeros}
                        />
                    )}

                    {activeTab === 'balance-sheet' && (
                        <BalanceSheetTab
                            lines={renderedBalanceLines}
                            year={filters.year}
                            comparisonYear={filters.comparisonYear}
                            onLineClick={handleViewLineClick}
                            onCommentClick={handleViewCommentClick}
                            loading={loading}
                            hideZeros={hideZeros}
                        />
                    )}

                    {activeTab === 'repostings' && (
                        <RepostingsTab
                            company={filters.company}
                            year={filters.year}
                            canEdit={canEdit}
                            onRepostingsChanged={reload}
                        />
                    )}

                    {activeTab === 'statement-views' && (
                        <ViewBuilderTab
                            company={filters.company}
                            canEdit={canEdit}
                            onViewsChanged={() => { reloadViews(); reload(); }}
                        />
                    )}

                    {activeTab === 'line-config' && (
                        <LineConfigTab canEdit={canEdit} company={filters.company} />
                    )}

                    {activeTab === 'account-mapping' && (
                        <AccountMappingTab
                            company={filters.company}
                            year={filters.year}
                            canEdit={canEdit}
                        />
                    )}
                </div>
            </div>

            {/* Drillthrough L1: Line Accounts */}
            {drillLine && (
                <DrillThroughModal
                    isOpen={!!drillLine}
                    onClose={() => setDrillLine(null)}
                    line={drillLine}
                    company={filters.company}
                    year={filters.year}
                    comparisonYear={filters.comparisonYear}
                    onAccountClick={handleAccountClick}
                    onCommentClick={handleDrillCommentClick}
                />
            )}

            {/* Drillthrough L2: Account Transactions */}
            {drillAccount && (
                <TransactionDrillModal
                    isOpen={!!drillAccount}
                    onClose={() => setDrillAccount(null)}
                    accountNr={drillAccount.nr}
                    accountName={drillAccount.name}
                    company={filters.company}
                    year={filters.year}
                    comparisonYear={filters.comparisonYear}
                />
            )}

            {/* Comment Panel */}
            {commentScope && (
                <CommentPanel
                    isOpen={!!commentScope}
                    onClose={() => setCommentScope(null)}
                    company={filters.company}
                    year={filters.year}
                    lineNr={commentScope.lineNr}
                    accountNr={commentScope.accountNr}
                    scopeLabel={commentScope.label}
                    currentUserEmail={user?.email || ''}
                    canEdit={canEdit}
                    onCountChanged={reload}
                />
            )}
        </div>
    );
}
